/**
 * Model kolumn widoku SQL dla konsumentów spoza karty rekordu (edytor układu kolumn i podgląd w
 * kreatorze formatki): stan odczytu kolumn, deskryptory, tytuł widoku i powód błędu — jedno
 * miejsce zamiast powielania tego samego składania w każdym konsumencie (odpowiednik
 * `use-additional-table-columns-model` dla tabel dodatkowych).
 *
 * Kolumny widoku są znane dopiero po uruchomieniu zapytania, więc model wykonuje je po stronie
 * serwera (`useAdditionalViewColumns`). Gdy baza odrzuci zapytanie (np. „division by zero"),
 * powód z serwera trafia do `errorReason` — kreator pokazuje go adminowi (budują go tylko
 * administratorzy).
 */

import { useMemo } from 'react';
import type { EmbeddedTableColumnDescriptor } from '@saas/shared';
import { isAxiosError } from 'axios';

import { useAdditionalViewColumns, useAdditionalViewsList } from '@/lib/additional-views/api';

import { additionalViewDescriptors } from './additional-view-descriptors';

/** Stan odczytu kolumn widoku: ładowanie, błąd, brak widoku, gotowe. */
export type AdditionalViewColumnsStatus = 'pending' | 'error' | 'notFound' | 'ready';

export interface AdditionalViewColumnsModel {
  /** Stan odczytu kolumn widoku. */
  status: AdditionalViewColumnsStatus;
  /** Deskryptory kolumn w kolejności zapytania; pusta tablica, dopóki `status` ≠ `'ready'`. */
  descriptors: EmbeddedTableColumnDescriptor[];
  /** Nazwa widoku z katalogu (`displayName`); `undefined`, dopóki katalog się nie załaduje albo gdy widoku brak. */
  title: string | undefined;
  /**
   * Powód błędu od serwera (`message` odpowiedzi 400/422 — powód odrzucenia zapytania przez
   * bazę); tylko przy `status === 'error'` i tylko gdy serwer go podał.
   */
  errorReason?: string;
  /** Błąd zapytania — tylko przy `status === 'error'`. */
  error?: unknown;
}

/** Czy błąd to odpowiedź HTTP 404 serwera (widok nie istnieje albo nie należy do tenanta). */
function isNotFoundResponse(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

/** Niepusty `message` z ciała odpowiedzi błędu (powód od serwera/bazy); inaczej `undefined`. */
function serverReason(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== '' ? message : undefined;
}

/**
 * Kolumny, deskryptory i tytuł widoku SQL po slugu. `descriptors` jest memoizowane po tablicy
 * kolumn, więc zmienia tożsamość tylko wraz z danymi (bezpieczne w zależnościach hooków).
 *
 * `notFound` = katalog załadowany, ale brak w nim widoku o tym slugu (albo slug pusty), albo
 * serwer odpowiedział 404. `error` tylko wtedy, gdy zapytanie nie ma żadnych danych: nieudane
 * odświeżenie w tle przy danych z cache nie psuje wyniku (`ready` zostaje).
 */
export function useAdditionalViewColumnsModel(
  viewSlug: string | undefined,
): AdditionalViewColumnsModel {
  const viewsQuery = useAdditionalViewsList();
  const view =
    viewSlug === undefined || viewSlug === ''
      ? undefined
      : viewsQuery.data?.find((candidate) => candidate.slug === viewSlug);
  const columnsQuery = useAdditionalViewColumns(view?.id);
  const columns = columnsQuery.data;
  const descriptors = useMemo(
    () => (columns === undefined ? [] : additionalViewDescriptors(columns)),
    [columns],
  );
  const title = view?.displayName;

  if (viewsQuery.data === undefined) {
    return viewsQuery.isError
      ? { status: 'error', descriptors, title, error: viewsQuery.error }
      : { status: 'pending', descriptors, title };
  }
  if (view === undefined) return { status: 'notFound', descriptors, title };
  if (columns === undefined) {
    if (!columnsQuery.isError) return { status: 'pending', descriptors, title };
    if (isNotFoundResponse(columnsQuery.error)) return { status: 'notFound', descriptors, title };
    const errorReason = serverReason(columnsQuery.error);
    return {
      status: 'error',
      descriptors,
      title,
      error: columnsQuery.error,
      ...(errorReason !== undefined ? { errorReason } : {}),
    };
  }
  return { status: 'ready', descriptors, title };
}
